import {
	liveSessionEnabled,
	proethicaCollectionIdByModule,
} from "@constants/demo";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { fetchUserSessions } from "../api/mutations";
import type { UserSession } from "../api/types";
import {
	type ModuleSession,
	useProethicaSessionStore,
} from "./useProethicaSessionStore";

export const PROETHICA_MODULE_SESSIONS_KEY = "proethica-module-sessions";

// One entry per known module: a ModuleSession to set, or null to clear ("Start").
// The whole map comes from a single backend snapshot, so it's all-or-nothing -
// if the fetch throws, useQuery yields no data and the tracker is left untouched.
type ReconcileMap = Record<string, ModuleSession | null>;

const pickModuleSession = (sessions: UserSession[]): ModuleSession | null => {
	const summative = sessions
		.filter((session) => session.sessionType === "SUMMATIVE")
		.sort((a, b) => (a.startTime < b.startTime ? 1 : -1));
	const chosen =
		summative.find((session) => session.status === "ACTIVE") ??
		summative.find((session) => session.status === "COMPLETE");
	return chosen ? { numericId: chosen.numericId, status: chosen.status } : null;
};

// One call returns every collection's sessions (keyed by sessionType), each
// tagged with collectionId, so we group locally instead of fetching per module.
const fetchModuleSessions = async (userId: number): Promise<ReconcileMap> => {
	const grouped = await fetchUserSessions({ userId });
	const sessionsByCollection = new Map<number, UserSession[]>();
	for (const session of Object.values(grouped ?? {}).flat()) {
		const list = sessionsByCollection.get(session.collectionId) ?? [];
		list.push(session);
		sessionsByCollection.set(session.collectionId, list);
	}

	return Object.fromEntries(
		Object.entries(proethicaCollectionIdByModule).map(
			([moduleId, collectionId]) => [
				moduleId,
				pickModuleSession(sessionsByCollection.get(collectionId) ?? []),
			],
		),
	);
};

// Reconciles the persisted per-module tracker against the backend session list
// on app load (and whenever the query is invalidated after create/end). The
// backend is the source of truth: a server-side deleted session disappears from
// the list, so the module reverts to "Start". Mounted once at the dashboard
// layout.
export const useProethicaSessionSync = () => {
	const { userId } = useAppConfigIds();
	const examId = useDemoExamStore((state) => state.currentExam?.id);
	const setModuleSession = useProethicaSessionStore(
		(state) => state.setModuleSession,
	);
	const clearModuleSession = useProethicaSessionStore(
		(state) => state.clearModuleSession,
	);

	const { data } = useQuery({
		queryKey: [PROETHICA_MODULE_SESSIONS_KEY, userId],
		queryFn: () => fetchModuleSessions(userId),
		enabled: liveSessionEnabled(examId) && !!userId,
	});

	useEffect(() => {
		if (!data) {
			return;
		}
		for (const [moduleId, session] of Object.entries(data)) {
			if (session) {
				setModuleSession(moduleId, session);
			} else {
				clearModuleSession(moduleId);
			}
		}
	}, [data, setModuleSession, clearModuleSession]);
};
